export interface ColorPaletteOption {
  id: string;
  name: string;
  category: string;
  colors: string[];
  keywords: string;
  mood: string;
}

export interface CustomPalette {
  name: string;
  colors: string[];
  keywords: string;
}

const CATEGORY_BASES: Record<string, { mood: string; tones: string[]; colors: string[][] }> = {
  Cinematic: {
    mood: 'cinematic',
    tones: ['Golden Sunset', 'Blue Hour', 'Noir Contrast', 'Teal Orange', 'Storm Gray'],
    colors: [['#FFB347', '#FFCC33', '#FF9966'], ['#355C7D', '#6C5B7B', '#C06C84'], ['#0F0F1A', '#3B3B52', '#B8B8C9'], ['#0F4C75', '#F08A5D', '#F9ED69'], ['#2B2D42', '#4A4E69', '#8D99AE']],
  },
  Nature: {
    mood: 'natural',
    tones: ['Forest Mist', 'Ocean Breeze', 'Desert Heat', 'Arctic Ice', 'Autumn Grove'],
    colors: [['#2D6A4F', '#95D5B2', '#D8F3DC'], ['#0077B6', '#90E0EF', '#CAF0F8'], ['#E76F51', '#F4A261', '#E9C46A'], ['#8ECAE6', '#219EBC', '#023047'], ['#9C6644', '#D4A373', '#E9EDC9']],
  },
  Cyberpunk: {
    mood: 'futuristic',
    tones: ['Cyberpunk Neon', 'Neon Tokyo', 'Synthwave Grid', 'Electric Pulse', 'Quantum Violet'],
    colors: [['#FF00FF', '#00FFFF', '#0A0A0A'], ['#FF0054', '#00D9FF', '#FFD60A'], ['#6A00F4', '#F20089', '#00F5D4'], ['#39FF14', '#00B4D8', '#03045E'], ['#9D4EDD', '#C77DFF', '#240046']],
  },
  Pastel: {
    mood: 'soft',
    tones: ['Pastel Dream', 'Candy Pop', 'Blush Cloud', 'Mint Breeze', 'Lavender Milk'],
    colors: [['#FFD6E0', '#CDE7FF', '#E4C1F9'], ['#FF99C8', '#FCF6BD', '#A9DEF9'], ['#FFC8DD', '#FFAFCC', '#BDE0FE'], ['#B8F2E6', '#AED9E0', '#F6FFF8'], ['#CDB4DB', '#FFC8DD', '#BDE0FE']],
  },
  Vintage: {
    mood: 'retro',
    tones: ['Vintage Film', 'Sepia Dust', 'Retro Polaroid', 'Muted Olive', 'Old Postcard'],
    colors: [['#6B4F4F', '#C9ADA7', '#F2E9E4'], ['#704214', '#C2A878', '#E6CCB2'], ['#D8A47F', '#EFC69B', '#F8EDEB'], ['#556B2F', '#A3B18A', '#DAD7CD'], ['#8D6E63', '#C7B198', '#F1E9DA']],
  },
  Film: {
    mood: 'cinema',
    tones: ['Kodak Warmth', 'Fuji Cool', 'Monochrome Drama', 'Retro Reversal', 'Studio Neutral'],
    colors: [['#F4A261', '#E76F51', '#2A9D8F'], ['#577590', '#43AA8B', '#90BE6D'], ['#22223B', '#4A4E69', '#F2E9E4'], ['#9A8C98', '#C9ADA7', '#F2E9E4'], ['#ADB5BD', '#DEE2E6', '#6C757D']],
  },
  Fantasy: {
    mood: 'mythic',
    tones: ['Mythic Dawn', 'Arcane Violet', 'Dragon Ember', 'Elven Glow', 'Celestial Bloom'],
    colors: [['#FFD166', '#EF476F', '#118AB2'], ['#5A189A', '#9D4EDD', '#E0AAFF'], ['#FF6B35', '#F7B801', '#7B2D26'], ['#80FFDB', '#64DFDF', '#5390D9'], ['#B5179E', '#7209B7', '#4CC9F0']],
  },
  Dark: {
    mood: 'moody',
    tones: ['Midnight Ash', 'Obsidian Mist', 'Eerie Green', 'Crimson Void', 'Steel Shadow'],
    colors: [['#0B090A', '#161A1D', '#660708'], ['#1B263B', '#415A77', '#778DA9'], ['#081C15', '#1B4332', '#2D6A4F'], ['#2B2D42', '#8D99AE', '#D90429'], ['#212529', '#343A40', '#495057']],
  },
  Neon: {
    mood: 'vibrant',
    tones: ['Laser Lime', 'Magenta Burst', 'Neon Aurora', 'Toxic Glow', 'Urban Pulse'],
    colors: [['#B9FBC0', '#98F5E1', '#8EECF5'], ['#FF4D6D', '#C9184A', '#7B2CBF'], ['#00F5D4', '#00BBF9', '#9B5DE5'], ['#70E000', '#38B000', '#008000'], ['#3A86FF', '#8338EC', '#FF006E']],
  },
  Anime: {
    mood: 'animated',
    tones: ['Shonen Hero', 'Magical Girl', 'Slice of Life', 'Mecha Core', 'Ghibli Pasture'],
    colors: [['#FF595E', '#FFCA3A', '#8AC926'], ['#FF8FAB', '#FFC6FF', '#BDE0FE'], ['#A2D2FF', '#BDE0FE', '#FFAFCC'], ['#6D597A', '#355070', '#B56576'], ['#588157', '#A3B18A', '#DAD7CD']],
  },
};

function shade(hex: string, factor: number): string {
  const value = hex.replace('#', '');
  const num = Number.parseInt(value, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const mix = (c: number) => Math.max(0, Math.min(255, Math.round(c + (255 - c) * factor)));
  return `#${[mix(r), mix(g), mix(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

export const BASE_COLOR_PALETTES: ColorPaletteOption[] = Object.entries(CATEGORY_BASES).flatMap(([category, config]) => {
  return config.tones.map((tone, index) => ({
    id: `${category.toLowerCase()}-${tone.toLowerCase().replace(/\s+/g, '-')}`,
    name: tone,
    category,
    colors: config.colors[index],
    keywords: `${config.mood} ${tone.toLowerCase()} palette, ${config.colors[index].length} color harmony`,
    mood: config.mood,
  }));
});

const VARIANT_LABELS = ['Soft', 'Bold', 'Mist', 'Glow', 'Noir', 'Ultra', 'Dusk', 'Dawn', 'Prime', 'Edge', 'Crisp', 'Muted', 'Velvet', 'Radiant'];

export const COLOR_PALETTES: ColorPaletteOption[] = BASE_COLOR_PALETTES.flatMap((palette) => {
  return VARIANT_LABELS.flatMap((variant, variantIndex) => {
    const factor = (variantIndex - 5) / 18;
    const shifted = palette.colors.map(c => shade(c, factor));
    return [
      {
        id: `${palette.id}-${variant.toLowerCase()}`,
        name: `${palette.name} ${variant}`,
        category: palette.category,
        colors: shifted,
        keywords: `${palette.keywords}, ${variant.toLowerCase()} tone, ${palette.mood} mood`,
        mood: palette.mood,
      },
      {
        id: `${palette.id}-${variant.toLowerCase()}-alt`,
        name: `${palette.name} ${variant} Alt`,
        category: palette.category,
        colors: [...shifted].reverse(),
        keywords: `${palette.keywords}, alternate arrangement, ${palette.mood} visual mood`,
        mood: palette.mood,
      },
    ];
  });
});

export const PALETTE_PRESETS: ColorPaletteOption[] = [
  { id: 'none', name: 'None', category: 'System', colors: ['#374151', '#6B7280', '#9CA3AF'], keywords: '', mood: 'neutral' },
  ...COLOR_PALETTES,
];

export const PALETTE_COUNT = COLOR_PALETTES.length;


const COLOR_NAME_MAP: { name: string; hex: string }[] = [
  { name: 'red', hex: '#ff0000' },
  { name: 'orange', hex: '#ff8c00' },
  { name: 'yellow', hex: '#ffd700' },
  { name: 'green', hex: '#2e8b57' },
  { name: 'cyan', hex: '#00bcd4' },
  { name: 'blue', hex: '#1e40af' },
  { name: 'purple', hex: '#7c3aed' },
  { name: 'pink', hex: '#ec4899' },
  { name: 'brown', hex: '#8b5a2b' },
  { name: 'gray', hex: '#6b7280' },
  { name: 'black', hex: '#111111' },
  { name: 'white', hex: '#f8fafc' },
];

const hexDistance = (a: string, b: string): number => {
  const cleanA = a.replace('#', '');
  const cleanB = b.replace('#', '');
  const av = Number.parseInt(cleanA, 16);
  const bv = Number.parseInt(cleanB, 16);
  const ar = (av >> 16) & 255;
  const ag = (av >> 8) & 255;
  const ab = av & 255;
  const br = (bv >> 16) & 255;
  const bg = (bv >> 8) & 255;
  const bb = bv & 255;
  return Math.sqrt((ar - br) ** 2 + (ag - bg) ** 2 + (ab - bb) ** 2);
};

export const describeHexColor = (hex: string): string => {
  const clean = /^#[0-9a-fA-F]{6}$/.test(hex.trim()) ? hex.trim().toLowerCase() : '#808080';
  const nearest = COLOR_NAME_MAP.reduce((best, color) => {
    const dist = hexDistance(clean, color.hex);
    return dist < best.dist ? { color, dist } : best;
  }, { color: COLOR_NAME_MAP[0], dist: Number.POSITIVE_INFINITY });
  return nearest.color.name;
};

export const palettePromptWithCodes = (keywords: string, colors: string[]): string => {
  const normalized = colors
    .map((color) => color.trim().toLowerCase())
    .filter((color) => /^#[0-9a-f]{6}$/.test(color))
    .slice(0, 5);

  const colorTokens = normalized.map((hex) => `${describeHexColor(hex)} (${hex})`);
  const codesOnly = normalized.join(', ');
  const colorDescriptor = colorTokens.length
    ? `Colors: ${colorTokens.join(', ')}. HEX codes: ${codesOnly}`
    : '';

  if (keywords.trim() && colorDescriptor) return `${keywords.trim()}. ${colorDescriptor}`;
  if (keywords.trim()) return keywords.trim();
  return colorDescriptor || 'balanced cinematic palette';
};

export const palettePromptFromCustom = (palette: CustomPalette): string => {
  const names = palette.colors.slice(0, 5).join(', ');
  const fallback = `color palette using ${names}`;
  return palettePromptWithCodes(palette.keywords.trim() || fallback, palette.colors);
};
